import { useEffect, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import { useLista, useManagedContext } from "../../components/hooks"
import { usePermissions } from "../../components/hooks/usePermissions"
import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from "../../config/apis"
import { ServerRequest } from "../../utils/apiweb"
import { onRequestError, onRequestNoSuccess } from "../../utils/requests"
import { SectionHeading, TableCustom, WrapperAdvancedSearch, FooterButtonVolver } from "../../components/common"
import TipoControladorModal from "../../components/controls/TipoControladorModal"
import { PERMISSIONS } from "../../consts/permissions"
import { useSearchParams } from "react-router-dom"
const TipoControladoresView = () => {
    const { setIsLoading, showMessageModal } = useManagedContext()
    const location = useLocation()
    const previousFilters = location.state?.filters

    const [tipos, setTipos] = useState([])
    const [tableVisible, setTableVisible] = useState(false)
    const [editMode, setEditMode] = useState()
    const [editItem, setEditItem] = useState()

    const searchRef = useRef()
    
    const { hasPermission } = usePermissions();
    const [searchParams] = useSearchParams();
    const canEdit = hasPermission(PERMISSIONS.LISTAS_INGRESOS_PUBLICOS_EDIT);
    const isViewOnly = searchParams.get('mode') === OPERATION_MODE.VIEW;
    const allowEditActions = canEdit && !isViewOnly;

    const [_, getRowLista ] = useLista({
        listas: ['TipoTributo'],
        onLoaded: (_, isSuccess, error) => {
          if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error)
        },
        memo: {
          key: 'TipoTributo',
          timeout: 0
        }
    })

    const filterData = data => {
        const { codigo, nombre, idTipoTributo, esSupervisor } = searchRef.current.filters

        let filtered = [...data]

        if (codigo !== '') filtered = filtered.filter(item => item.codigo.toLowerCase().includes(codigo.toLowerCase()))
        if (nombre !== '') filtered = filtered.filter(item => item.nombre.toLowerCase().includes(nombre.toLowerCase()))
        if (idTipoTributo !== 0) filtered = filtered.filter(item => item.idTipoTributo === idTipoTributo)
        if (esSupervisor) filtered = filtered.filter(item => item.esSupervisor)

        return filtered
    }

    const SearchTipoControladores = () => {
        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.TIPO_CONTROLADOR,
            null,
            null,
            res => res.json().then(data => {
                setTipos(filterData(data))
                setIsLoading(false)
                setTableVisible(true)
                setEditMode(null)
                setEditItem(null)
            }),
            res => {
                onRequestNoSuccess(res)
                setIsLoading(false)
                setEditMode(null)
                setEditItem(null)
            },
            err => {
                onRequestError(err)
                setIsLoading(false)
                setEditMode(null)
                setEditItem(null)
            },
        )
    }

    const confirmRemoveId = (id) => {
        setIsLoading(true)
        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.TIPO_CONTROLADOR,
            `/${id}`,
            null,
            () => {
                SearchTipoControladores()
            },
            res => {
                onRequestNoSuccess(res)
                setIsLoading(false)
            },
            err => {
                onRequestError(err)
                setIsLoading(false)
            },
        )
    }

    const avmr = {
        onAdd: () => {
            setEditMode('add')
            setEditItem({
                id: -Date.now(),
                codigo: '',
                nombre: '',
                orden: '',
                idTipoTributo: 0,
                esSupervisor: false,
                email: false,
                direccion: false,
                abogado: false,
                oficialJusticia: false,
            })
        },
        onView: (item) => {
            setEditMode('view')
            setEditItem(item)
        },
        onModify: (item) => {
            setEditMode('modify')
            setEditItem(item)
        },
        onRemove: ({ id }) => {
            showMessageModal({
                title: "Confirmación",
                message: "¿Está seguro de borrar el registro?",
                onConfirm: () => confirmRemoveId(id),
            })
        }
    }

    const onModalCancel = () => {
        setEditMode(null)
        setEditItem(null)
    }

    const onModalConfirm = () => {
        if (tableVisible) SearchTipoControladores()
        else {
            setEditMode(null)
            setEditItem(null)
        }
    }

    return <>
        <SectionHeading titles={[{ title: 'Entidades' }, { title: 'Administración de listas', url: '/entidades' }, { title: 'Tipos de controladores' }]}
                       onAddClick={allowEditActions ? avmr.onAdd : null} {...{tableVisible}} />

        <section className='section-accordion'>
            <WrapperAdvancedSearch
                ref={searchRef}
                filters={[
                    { title: 'Código', field: 'codigo', type: 'string', size: 3 },
                    { title: 'Nombre', field: 'nombre', type: 'string', size: 3 },
                    { title: 'Tipo de tributo', field: 'idTipoTributo', type: 'list', lista: 'TipoTributo', size: 3 },
                    {
                        title: 'Tipo',
                        field: 'esSupervisor',
                        type: 'segmented',
                        size: 3,
                        options: [
                            { value: null, label: 'Todos' },
                            { value: true, label: 'Supervisor/a' },
                        ],
                    },
                ]}
                onSearch={SearchTipoControladores}
            />

            {tableVisible && (
                <TableCustom
                    className={'TableCustomBase m-top-20'}
                    data={tipos}
                    avmr={avmr}
                    editCode={PERMISSIONS.LISTAS_INGRESOS_PUBLICOS_EDIT}
                    disabled={!allowEditActions}
                    messageEmpty="No se encontraron resultados"
                    columns={[
                        { Header: 'Código', accessor: 'codigo', width: '20%', },
                        { Header: 'Nombre', accessor: 'nombre', width: '20%', },
                        { Header: 'Tipo de tributo', accessor: 'idTipoTributo', width: '20%', Cell: ({ value }) =>  getRowLista('TipoTributo', value)?.nombre ?? '' },
                        { Header: 'Supervisor/a', accessor: 'esSupervisor', width: '20%', },
                    ]}
                />
            )}

            {editItem && (
                <TipoControladorModal
                    item={editItem}
                    editMode={editMode}
                    onConfirm={onModalConfirm}
                    onCancel={onModalCancel}
                />
            )}
        </section>
        <footer className='footer footer-action'>
            <div className='footer-action-container'>
                <FooterButtonVolver filters={previousFilters} to="/entidades" />
            </div>
        </footer>
    </>
}

export default TipoControladoresView
